import type { IconNames } from "@/v2-ui/icon/icon.types"
import type { BadgeVariant } from "@/v2-backoffice-client/components/badge"

// The trials report (Activity → Trials): the live replacement for the weekly
// hand-made "trial users" post. Everything here reads the license domain
// (lic.Licenses, status Trial) and the usage profiles — not CRM fields.

/** A trial that started within this many days reads as new this week. */
export const TRIAL_NEW_DAYS = 7
/** A trial ending within this many days reads as ending soon. */
export const TRIAL_ENDING_SOON_DAYS = 7

/**
 * How a trialing customer is using the product right now — the report's
 * first question. Engaged: someone was active in the last 7 days. Cooling:
 * activity in the last 28 days, nobody in the last 7. Silent: seats, but no
 * tracked activity in 28 days. No users: nobody is on the customer yet.
 */
export type TrialPulse = "engaged" | "cooling" | "silent" | "no-users"

export const TRIAL_PULSE_ORDER: TrialPulse[] = [
  "engaged",
  "cooling",
  "silent",
  "no-users",
]

export const TRIAL_PULSE_LABELS: Record<TrialPulse, string> = {
  engaged: "Engaged",
  cooling: "Cooling",
  silent: "Silent",
  "no-users": "No users yet",
}

export const TRIAL_PULSE_DESCRIPTIONS: Record<TrialPulse, string> = {
  engaged: "Someone on the trial was active in the last 7 days.",
  cooling: "Activity in the last 28 days, but nobody in the last 7.",
  silent: "People have access, but nobody has used it in the last 28 days.",
  "no-users":
    "The trial has no users yet — nobody has been invited or signed up.",
}

export const TRIAL_PULSE_VISUALS: Record<
  TrialPulse,
  { variant: BadgeVariant; iconSrc: IconNames }
> = {
  engaged: { variant: "positive", iconSrc: "check-circle" },
  cooling: { variant: "warning", iconSrc: "clock" },
  silent: { variant: "critical", iconSrc: "moon" },
  "no-users": { variant: "neutral", iconSrc: "user-slash" },
}

/**
 * Where the trial clock stands: overdue (past its end date, still a trial),
 * ending soon, running, not started (start date ahead) or open-ended (no
 * end date set).
 */
export type TrialClockState =
  | "overdue"
  | "ending-soon"
  | "running"
  | "not-started"
  | "open-ended"

export const TRIAL_CLOCK_LABELS: Record<TrialClockState, string> = {
  overdue: "Overdue",
  "ending-soon": "Ending soon",
  running: "Running",
  "not-started": "Not started",
  "open-ended": "No end date",
}

/** The status pills' URL param and the search box's, so a view is linkable. */
export const TRIALS_STATUS_PARAM = "trials-status"
export const TRIALS_SEARCH_PARAM = "trials-q"
/** The cohort activity chart's own timeframe param (see usePortfolioTimeframe). */
export const TRIALS_WINDOW_PARAM = "trials-window"

/** Status-pill values: the pulse buckets, then the clock buckets, then new. */
export const TRIALS_PILL_VALUES = {
  ENGAGED: 1,
  COOLING: 2,
  SILENT: 3,
  NO_USERS: 4,
  ENDING_SOON: 5,
  OVERDUE: 6,
  NEW: 7,
} as const

/** The pill's filter value for the row's "new this week" column. */
export const TRIAL_NEW_FILTER_VALUE = "new"
